/**
 * Deterministic stimulus randomness.
 *
 * Every stimulus must be reproducible from `(formId, trialIndex, difficulty)` alone, so a
 * trial can be re-scored years later from the event record without the client build that
 * produced it. `trial_start` also stores the stimulus, which makes the two independently
 * checkable — a mismatch means client and server disagree about calibration, which is
 * exactly the silent corruption v2 §18 forbids.
 *
 * Extracted from `index.ts` so every generator draws from the same source. Two generators
 * with their own PRNGs would be two calibrations to keep in step.
 */
export function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
export function seedFrom(formId, trialIndex, difficulty) {
    let h = 2166136261 >>> 0;
    const s = `${formId}:${trialIndex}:${difficulty}`;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h;
}
/** Seeded Fisher-Yates. Returns a new array; the input is left alone. */
export function shuffle(items, rand) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const a = out[i];
        const b = out[j];
        if (a === undefined || b === undefined)
            continue;
        out[i] = b;
        out[j] = a;
    }
    return out;
}
/**
 * Lays the answer and the wrong answers out as cards, so that WHERE a card sits says nothing.
 *
 * **Every choice game had the same leak and it took a child's eye to describe it.** Each one built
 * its distractors in a deliberate order — the near miss first, then the next-nearest, then the rest
 * — and then spliced the answer in at a drawn index. The answer's slot was therefore uniform, which
 * is what everyone checked, and the WRONG cards were in a fixed order, which nobody did. So the
 * hardest distractor was always the earliest wrong card, the tokens already in the pattern always
 * came before the invented ones, and a child could learn the shape of the row instead of the
 * question. Five-year-olds are extremely good at learning the shape of a row.
 *
 * Shuffling the whole set is the only version of this that is safe by construction. Placing the
 * answer randomly is not enough, because it leaves the rest of the arrangement carrying whatever
 * order the generator happened to build it in.
 *
 * This says nothing about the VALUES on the cards, which is a separate leak with a separate fix —
 * see the offsets in `math.ts`, where the wrong answers used to straddle the right one so reliably
 * that "pick the middle number" beat the division level every time.
 */
export function layOutOptions(answer, distractors, rand) {
    const cards = shuffle([{ value: answer, isAnswer: true }, ...distractors.map((value) => ({ value, isAnswer: false }))], rand);
    return {
        options: cards.map((card) => card.value),
        correctIndex: cards.findIndex((card) => card.isAnswer),
    };
}
/**
 * Caps how many times the same value may appear in a row.
 *
 * **Independent draws produce runs, and a run reads as a broken game.** Five cards in a row showing
 * the same shape is a perfectly ordinary outcome of a fair coin and it looks like the generator has
 * stopped working — which is what it was reported as. The sorting game has only two cards by design,
 * so its runs are the most visible; the same thing happens anywhere a short alphabet is drawn from
 * repeatedly.
 *
 * The fix is not "more randomness" — it is less. A capped run is a small, deliberate departure from
 * independence, applied after the draw so the underlying distribution is otherwise untouched: only
 * the element that would have extended a too-long run is changed, and it is changed to another value
 * from the same pool.
 *
 * Deterministic, like every draw here: it takes the same PRNG the caller was already using, so a
 * trial stays reproducible from `(formId, trialIndex, difficulty)` alone.
 *
 * @param values   the drawn sequence, left otherwise as it is
 * @param maxRun   how many identical values may stand together
 * @param pool     every value that could legally appear
 */
export function capRuns(values, maxRun, pool, rand) {
    if (maxRun < 1 || pool.length < 2)
        return values;
    const out = [...values];
    let run = 1;
    for (let i = 1; i < out.length; i++) {
        if (out[i] !== out[i - 1]) {
            run = 1;
            continue;
        }
        run++;
        if (run <= maxRun)
            continue;
        // Anything but the value that is repeating. Drawn rather than taken in order, so the break does
        // not always land on the same replacement and become a pattern of its own.
        const others = pool.filter((value) => value !== out[i]);
        out[i] = others[Math.floor(rand() * others.length)];
        run = 1;
    }
    return out;
}
//# sourceMappingURL=random.js.map